import React, { useEffect, type ReactNode } from 'react'
import { Global } from '@emotion/core'
import { MarginBox, Tab, Tabs } from '@fs/zion-ui'
import { getLanguage } from '@fs/zion-standards'
import { langText } from '@fs/zion-locale'
import Markdown from 'markdown-to-jsx'
import { useTranslation } from 'react-i18next'
import { Link } from '@fs/zion-router'
import ContentWrapper from '../ContentWrapper'
import { createMarkdownStyles } from '../../../assets/markdownStyles'
import useHandbookMarkdownContent from '../hooks/useHandbookMarkdownContent'

const markdownStyles = createMarkdownStyles('handbook-markdown')

// Get language display name from locale code
function getLanguageTitle(locale: string): string {
  return (
    langText.core.find((l) => l.code === locale)?.text ||
    langText.additional.find((l) => l.code === locale)?.text ||
    getLanguage(locale)?.primaryText ||
    locale
  )
}

type MarkdownLinkProps = Readonly<{
  href?: string
  children: ReactNode
}>

// Custom link component that uses React Router for internal links
const MarkdownLink = ({ href, children, ...props }: MarkdownLinkProps) => {
  // If it's an internal link (starts with /), use React Router
  if (href?.startsWith('/')) {
    return (
      <Link to={href} {...props}>
        {children}
      </Link>
    )
  }
  // External links use normal anchor tags
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
      {children}
    </a>
  )
}

type EditorHandbookContentProps = Readonly<{
  baseFilename: string
}>

export default function EditorHandbookContent({ baseFilename }: EditorHandbookContentProps): JSX.Element | null {
  const [t] = useTranslation()
  const contentRef = React.useRef<HTMLDivElement>(null)

  const { markdown, locales, selectedLocale, setSelectedLocale, error, loading } =
    useHandbookMarkdownContent(baseFilename)

  // Scroll to top when locale changes
  useEffect(() => {
    if (contentRef.current) {
      let scrollableParent: HTMLElement | null = contentRef.current
      let foundScrollableParent: HTMLElement | null = null

      while (scrollableParent) {
        const { overflow, overflowY } = window.getComputedStyle(scrollableParent)
        if (overflow === 'auto' || overflow === 'scroll' || overflowY === 'auto' || overflowY === 'scroll') {
          foundScrollableParent = scrollableParent
          break
        }
        scrollableParent = scrollableParent.parentElement
      }

      if (foundScrollableParent) {
        foundScrollableParent.scrollTop = 0
      } else {
        window.scrollTo(0, 0)
      }
    }
  }, [selectedLocale])

  if (loading) {
    return null
  }

  if (error) {
    return (
      <ContentWrapper>
        <MarginBox marginX="sm" marginY="sm">
          {t('editor-handbook.editor-handbook-content.loading-error', 'Error loading content.')}
        </MarginBox>
      </ContentWrapper>
    )
  }

  const showMarkdown = markdown && markdown.length > 0
  const index = locales?.indexOf(selectedLocale || '') ?? -1
  const selected = Math.max(index, 0)

  return (
    <ContentWrapper>
      <Global styles={markdownStyles} />
      <MarginBox marginX="sm" marginY="sm" ref={contentRef}>
        {locales?.length > 1 && (
          <Tabs
            index={selected}
            onChange={(ind) => {
              const newLocale = locales[ind]
              setSelectedLocale(newLocale)
            }}
          >
            {locales.map((locale) => {
              const title = getLanguageTitle(locale)
              return (
                <Tab key={locale} title={title}>
                  {showMarkdown && (
                    <div className="handbook-markdown">
                      <Markdown options={{ overrides: { a: MarkdownLink } }}>{markdown}</Markdown>
                    </div>
                  )}
                </Tab>
              )
            })}
          </Tabs>
        )}
        {showMarkdown && locales.length === 1 && (
          <div className="handbook-markdown">
            <Markdown options={{ overrides: { a: MarkdownLink } }}>{markdown}</Markdown>
          </div>
        )}
      </MarginBox>
    </ContentWrapper>
  )
}
